// Date / time helpers. Convention: every date crossing a function or DTO
// boundary in this codebase is an ISO 8601 string (e.g. `'2026-05-04T12:34:56.000Z'`).
// `Date` instances live only inside this file.
//
// Mirrors the surface of `investor-service`'s datetime module so frontend and
// backend speak the same vocabulary. The implementation is native (no dayjs)
// — if we ever need timezone-aware parsing, calendar-quarter math, or
// `endOf('day')` semantics, add dayjs in that PR; call sites stay the same.

// ── Durations ────────────────────────────────────────────────────────────
// Canonical millisecond constants. Use these for staleTime, gcTime, debounce
// intervals, setTimeout values. Importing here keeps every cache config and
// timeout in the codebase speaking the same vocabulary.
//
// The literals below are the *definitions* of those durations — this is the
// one block in the codebase where they're allowed.

export const ONE_SECOND_MS = 1000
export const THIRTY_SECONDS_MS = ONE_SECOND_MS * 30
export const ONE_MINUTE_MS = ONE_SECOND_MS * 60
export const TWO_MINUTES_MS = ONE_MINUTE_MS * 2
export const FIVE_MINUTES_MS = ONE_MINUTE_MS * 5
export const TEN_MINUTES_MS = ONE_MINUTE_MS * 10
export const ONE_HOUR_MS = ONE_MINUTE_MS * 60
export const ONE_DAY_MS = ONE_HOUR_MS * 24

/** Backend-symmetric alias for {@link ONE_DAY_MS}. */
export const MS_PER_DAY = ONE_DAY_MS

// ── Boundary parsing ─────────────────────────────────────────────────────
// Throws on invalid ISO strings so a malformed value fails loud at the
// boundary instead of producing NaN downstream.
const parseIso = (value: string): Date => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid ISO date string: ${value}`)
  }
  return date
}

// ── Now helpers ──────────────────────────────────────────────────────────

/** Current instant as an ISO 8601 string. */
export const nowIso = (): string => new Date().toISOString()

// ── Domain math ──────────────────────────────────────────────────────────

export const isFuture = (iso: string | null | undefined): boolean =>
  !!iso && parseIso(iso).getTime() > Date.now()

export const isPast = (iso: string | null | undefined): boolean =>
  !!iso && parseIso(iso).getTime() < Date.now()

/**
 * Days from now until `iso`. Past dates clamp to 0.
 * Returns null when `iso` is missing.
 */
export const daysFromNowUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null
  return Math.max(0, Math.ceil((parseIso(iso).getTime() - Date.now()) / ONE_DAY_MS))
}

/**
 * Days from now until `iso`, only when strictly in the future.
 * Returns null when `iso` is missing OR past/now.
 */
export const daysUntilFuture = (iso: string | null | undefined): number | null => {
  if (!iso) return null
  const delta = parseIso(iso).getTime() - Date.now()
  return delta > 0 ? Math.ceil(delta / ONE_DAY_MS) : null
}

/** ms from now until `iso` (positive when `iso` is in the future). */
export const msFromNow = (iso: string): number => parseIso(iso).getTime() - Date.now()

/** ms elapsed since `iso` (positive when `iso` is in the past). */
export const msSince = (iso: string): number => Date.now() - parseIso(iso).getTime()

// ── Formatters ───────────────────────────────────────────────────────────

/** "18 Mar 2026" */
export const formatDisplayDate = (date = new Date()): string =>
  date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })

/** "09:26 AM" */
export const formatDisplayTime = (date = new Date()): string =>
  date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

/** "18 Mar 2026 · 09:26 AM" — or "—" when the ISO string is absent */
export const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  return `${formatDisplayDate(date)} · ${formatDisplayTime(date)}`
}

/** "09 Apr 2026 · 14:35" — date in en-GB, time in 24h */
export const formatSubmittedAt = (date = new Date()): string => {
  const dateLabel = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const timeLabel = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${dateLabel} · ${timeLabel}`
}

/** Returns the current date and time as an ISO 8601 string. */
export const getCurrentDateTime = (): string => new Date().toISOString()

/** Locale-default formatting — used for free-form datetime display. */
export const formatDateTime = (dateTime: string): string => new Date(dateTime).toLocaleString()

/** "20 April 2026" — long month name, used in detail views and document displays. */
export const formatDate = (iso: string): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

/** "13/03/2026" — DD/MM/YYYY for table/list views. */
export const formatDateShort = (iso: string): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB')
}
